import { Fragment, type ReactNode } from "react";

/**
 * A warehouse name that may break onto another line after each underscore, and nowhere else
 * (walk 2026-10-02, D2, D7, D19): round 6 names are `<connection name>_<schema>_<table>`, 23 to
 * 45 characters, and a cut ("MOUSEFLOW_SANDBOX_FEED…") made two rows read alike. Each `_` is
 * followed by a `<wbr>`, so the text a screen reader, a copy or `getByText` reads is the whole name.
 *
 * The element that holds it takes `wrap-break-word` (`BREAKABLE_NAME_CLASS`): a part wider than its
 * column still breaks inside rather than overflow, and a name with no underscore is one word.
 */
export const BREAKABLE_NAME_CLASS = "whitespace-normal wrap-break-word";

export function breakableName(name: string): ReactNode {
  return breakAfter(name, /(?<=_)/);
}

/**
 * A dbt unique id (`test.project.not_null_orders_id.0123abcd45`) that may break after each `_`
 * and each `.` (walk of 9a9d586, D-h: the run page's dbt results cut long test ids at 1280 px with
 * no way to read the rest). Same rules as `breakableName`; the text stays whole.
 */
export function breakableNodeId(id: string): ReactNode {
  return breakAfter(id, /(?<=[_.])/);
}

function breakAfter(name: string, at: RegExp): ReactNode {
  const parts = name.split(at);
  if (parts.length < 2) return name;
  return parts.map((part, index) => (
    <Fragment key={index}>
      {index > 0 ? <wbr /> : null}
      {part}
    </Fragment>
  ));
}
